// The welcome offer: a small card that settles in once per visitor ("20% off your first order": `welcome.percent`
// in site.config.json), a few seconds after the page opens or a third of the way down it. Calm on purpose: it fades
// in without sliding across the screen, sits in a corner on desktop (clear of the buy button) and is a slim strip on
// phones. Not a modal: it never blocks the page, closes with Escape or "No thanks", stays away from help and policy
// pages, and never returns once closed or used. A link with ?welcome (patronus.co.za/?welcome) forgets that and shows it
// within a second, so the shop can see it on its own phone.
// The sign-up form inside is a normal [data-subscribe] form; the code comes back from our sign-up service.
const SEEN = 'patronus.welcome.v1';
const seen = () => { try { return !!localStorage.getItem(SEEN); } catch { return true; } };
const remember = (v) => { try { localStorage.setItem(SEEN, v); } catch {} };

/** delay: ms on the page before it shows; depth: the share of the page scrolled that shows it sooner, but not in
 *  the first `settle` ms, so a quick flick down the page doesn't bring it in while someone is still looking */
export function mountWelcome({ offer, delay = 6000, depth = 0.3, settle = 3000, doc = document, win = window }) {
  const again = new URLSearchParams(win.location.search).has('welcome');
  if (again) { try { localStorage.removeItem(SEEN); } catch {} }
  if (!offer?.percent || seen() || doc.querySelector('main.doc')) return null;
  const el = doc.createElement('aside');
  el.className = 'welcome'; el.hidden = true; el.setAttribute('aria-labelledby', 'welcomeTitle');
  el.innerHTML = `
    <button type="button" class="welcome-x" data-welcome-close aria-label="Close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M5 5l14 14M19 5 5 19"/></svg></button>
    <div class="welcome-ask">
      <h2 id="welcomeTitle">${offer.percent}% off your first order</h2>
      <p class="welcome-sub">Join our list for new pieces and their stories.</p>
      <form data-subscribe="newsletter" data-source="welcome" novalidate>
        <label class="sr" for="welcomeEmail">Email</label>
        <div class="welcome-row"><input id="welcomeEmail" type="email" data-clarity-mask="true" name="email" placeholder="Your email" autocomplete="email" required><button class="btn btn-navy">Get ${offer.percent}% off</button></div>
        <p class="form-msg" role="status" aria-live="polite"></p>
      </form>
      <p class="welcome-foot"><button type="button" class="welcome-no" data-welcome-close>No thanks</button><span>Unsubscribe any time. <a href="/policies/privacy">Privacy policy</a></span></p>
    </div>
    <div class="welcome-done" hidden>
      <h2>Here’s your ${offer.percent}% off</h2>
      <p class="welcome-code"><span data-welcome-code></span><button type="button" class="welcome-copy" data-welcome-copy>Copy</button></p>
      <p class="welcome-sub">It’s already in your bag, and works once, on your first order.</p>
      <button type="button" class="btn btn-navy" data-welcome-close>Keep shopping</button>
    </div>`;
  doc.body.appendChild(el);

  let timer = 0, shown = false;
  const busy = () => !!doc.querySelector('.drawer.on, .menu.on, .srch:not([hidden]), .lightbox:not([hidden]), .modal:not([hidden])');
  function show() {
    if (shown || seen()) return;
    if (busy()) { timer = setTimeout(show, 4000); return; }      // wait until the bag, menu or search is closed
    shown = true; cleanup(); el.hidden = false;
    void el.offsetWidth;                                          // lay it out hidden first, so the fade always plays
    el.classList.add('on');
    win.gtag?.('event', 'view_promotion', { promotion_name: `welcome-${offer.percent}` });
  }
  function close(why = 'closed') {
    remember(why); el.classList.remove('on'); setTimeout(() => { el.hidden = true; }, 300);
  }
  const opened = Date.now();
  const onScroll = () => { if (Date.now() - opened < settle) return; const max = doc.documentElement.scrollHeight - win.innerHeight; if (max > 0 && win.scrollY / max >= depth) show(); };
  const cleanup = () => { clearTimeout(timer); win.removeEventListener('scroll', onScroll); };
  timer = setTimeout(show, again ? 1000 : delay);
  win.addEventListener('scroll', onScroll, { passive: true });

  el.addEventListener('click', (e) => {
    if (e.target.closest('[data-welcome-close]')) return close(el.querySelector('.welcome-done').hidden ? 'dismissed' : 'joined');
    const cp = e.target.closest('[data-welcome-copy]');
    if (cp) { const code = el.querySelector('[data-welcome-code]').textContent; navigator.clipboard?.writeText(code).then(() => { cp.textContent = 'Copied'; }, () => {}); }
  });
  doc.addEventListener('keydown', (e) => { if (e.key === 'Escape' && el.classList.contains('on') && !busy()) close('dismissed'); });
  el.addEventListener('subscribed', (e) => {
    remember('joined');
    if (!e.detail.code) return close('joined');
    el.querySelector('[data-welcome-code]').textContent = e.detail.code;
    el.querySelector('.welcome-ask').hidden = true; el.querySelector('.welcome-done').hidden = false;
    el.querySelector('[data-welcome-copy]').focus();
  });
  return { show, close, el };
}
